import React, { useMemo, useRef, useState } from "react";
import { Button, Card, Popup } from "semantic-ui-react";
import { useSelector } from "react-redux";

import {
  TextBlock,
  createTextBlock,
  deleteTextBlock,
  updateTextBlock,
} from "../../api/textBlockApi";
import { Text } from "../../components/Text";
import { Language, LanguageCode } from "../../customers/customerlanguages";
import { popupDelay } from "../../customers/gpt/types";
import { LangString, LangStringObject } from "../../vocabulary/LangString";
import { debounce } from "../../utils/debounce";
import { RootState } from "../../utils/store";
// Same card chrome the Text generation tab uses for a generated text.
import { StyledCardContent } from "../copy-assistant/GeneratedResults/GeneratedSingleResult";
import { fixLongName } from "../copy-assistant/utils";
import { ProductId } from "../product";
import { TextBlockGroup } from "../textBlockGroups";

const SAVE_DEBOUNCE_MS = 800;

type Props = {
  productId: ProductId;
  group: TextBlockGroup;
  sourceLanguage?: Language;
  sectionName?: string;
  /** The language picked in the preview panel's dropdown; undefined while it hasn't loaded yet. */
  activeLanguage?: Language;
  promptName?: string;
  connectedChannelNames: string[];
  onSaved: () => void;
};

export const TranslationCard: React.FC<Props> = ({
  productId,
  group,
  sourceLanguage,
  sectionName,
  activeLanguage,
  promptName,
  connectedChannelNames,
  onSaved,
}) => {
  const token = useSelector((state: RootState) => state.auth.token);
  const [isDeleting, setIsDeleting] = useState(false);

  const isSource = activeLanguage?.code === group.source.language;
  const activeBlock: TextBlock | undefined = isSource
    ? group.source
    : activeLanguage
    ? group.translations[activeLanguage.code]
    : undefined;

  // LangString works on a flat language -> text map, so flatten the group into
  // one and map edits back onto the underlying TextBlock rows on save.
  const blockByLanguage = useMemo(() => {
    const byLanguage: Partial<Record<LanguageCode, TextBlock>> = {
      ...group.translations,
    };
    byLanguage[group.source.language] = group.source;
    return byLanguage;
  }, [group]);

  const initialData = useMemo(() => {
    const values: LangStringObject = {};
    Object.entries(blockByLanguage).forEach(([language, block]) => {
      values[language as LanguageCode] = block?.text ?? "";
    });
    return values;
  }, [blockByLanguage]);

  // The debounce timer lives in a ref so it survives re-renders rather than
  // restarting on every keypress -- but that means the closure would capture
  // the first render's blocks, so read them through a ref refreshed each render.
  const latest = useRef({ blockByLanguage, group, token, onSaved });
  latest.current = { blockByLanguage, group, token, onSaved };

  const persist = useRef(
    debounce(async (values: LangStringObject): Promise<void> => {
      const current = latest.current;
      const writes = Object.entries(values)
        // LangString injects a "__source__" pseudo-entry in template contexts.
        .filter(([language]) => !language.startsWith("__"))
        .map(async ([language, text]) => {
          const languageCode = language as LanguageCode;
          const existing = current.blockByLanguage[languageCode];
          if (existing) {
            if (existing.text === text) return;
            await updateTextBlock(current.token, { text }, existing.id);
            return;
          }
          if (!text) return;
          await createTextBlock(current.token, {
            product: current.group.source.product_id,
            section: current.group.source.section_id,
            text,
            language: languageCode,
            prompt_id: null,
            translated_from: current.group.source.id,
            active: true,
          });
        });
      await Promise.all(writes);
      current.onSaved();
    }, SAVE_DEBOUNCE_MS)
  );

  const handleDelete = async (): Promise<void> => {
    if (!activeBlock) return;
    setIsDeleting(true);
    try {
      await deleteTextBlock(token, activeBlock.id);
      onSaved();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Card fluid data-testid="translation-card">
      <StyledCardContent>
        <Popup
          disabled={!connectedChannelNames.length}
          size="small"
          wide
          content={`Section connected to ${connectedChannelNames.join(", ")}`}
          mouseEnterDelay={popupDelay}
          trigger={
            <div>
              <Text compact>{sectionName || "No section"}</Text>
              {!!connectedChannelNames.length && (
                <Text size="mini" compact color="grey">
                  Connected to: {fixLongName(connectedChannelNames.join(", "))}
                </Text>
              )}
            </div>
          }
        />
        <div className="card-extra-center"></div>
        <div></div>
      </StyledCardContent>

      <StyledCardContent style={{ paddingBlock: "10px" }}>
        <div>
          <Popup
            content="Remove text"
            size="small"
            trigger={
              <Button
                data-testid="translation-card-delete"
                disabled={isDeleting || !activeBlock}
                icon="trash alternate outline"
                compact
                basic
                floated="left"
                size="mini"
                onClick={handleDelete}
              />
            }
          />
        </div>
        <div className="card-extra-center"></div>
        <div>
          {isSource && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                height: "100%",
                alignItems: "center",
              }}
            >
              <Text color="grey" size="small" inline>
                Currently the source text
              </Text>
            </div>
          )}
        </div>
      </StyledCardContent>

      <Card.Content>
        <Card.Meta>
          <Text size="small">
            <b>Prompt: </b> {promptName ?? "Unknown"}
          </Text>
          {sourceLanguage && (
            <Text size="small" color="grey">
              <b>Source language: </b> {sourceLanguage.name}
            </Text>
          )}
        </Card.Meta>
        <Card.Description>
          {activeLanguage && (
            <LangString
              isMinified
              initialData={initialData}
              displayLanguage={activeLanguage.code}
              productId={productId}
              sectionId={group.source.section_id ?? undefined}
              sectionName={sectionName}
              textBlockId={activeBlock?.id}
              onDataChanged={(_container, values): void => {
                persist.current(values);
              }}
            />
          )}
        </Card.Description>
      </Card.Content>
    </Card>
  );
};
